import { useState } from "react";
import { Copy, Check, KeyRound, Webhook, Activity, FileText, ScrollText, Trash2, RefreshCw, Plus, Eye, EyeOff } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { SAMPLE_API_KEYS, SAMPLE_WEBHOOKS } from "../../lib/sample-data";
import { cn } from "../../lib/utils";

/**
 * Reusable API dashboard. Used by Agent AI page and Integrations.
 * Security:
 *  - Never displays plaintext API keys after creation.
 *  - All key values arrive masked from the backend.
 *  - "Reveal once" is a UX hint only — would call a one-shot backend endpoint.
 */
export default function ApiDashboard({ scope = "agent" }) {
  return (
    <div data-testid={`api-dashboard-${scope}`} className="space-y-6">
      <Tabs defaultValue="overview" className="w-full">
        <TabsList
          data-testid="api-tabs"
          className="grid grid-cols-3 md:grid-cols-6 gap-1 bg-[var(--pl-surface)]/60 border border-[var(--pl-border)] rounded-xl p-1 h-auto"
        >
          <Tab v="overview" icon={Activity}>Overview</Tab>
          <Tab v="billing" icon={FileText}>Billing</Tab>
          <Tab v="usage" icon={ScrollText}>Usage</Tab>
          <Tab v="keys" icon={KeyRound}>API Keys</Tab>
          <Tab v="webhooks" icon={Webhook}>Webhooks</Tab>
          <Tab v="docs" icon={FileText}>Docs</Tab>
        </TabsList>

        <TabsContent value="overview" className="mt-6 space-y-4" data-testid="apidash-overview">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Credits used" value="38,750" sub="of 50,000" />
            <Stat label="Connected APIs" value="7" sub="3 agents · 4 creative" />
            <Stat label="Recent requests" value="2,184" sub="last 24h" />
            <Stat label="Failed requests" value="3" sub="last 24h" tone="bad" />
          </div>
          <Card title="Active API Keys">
            <div className="text-sm text-[var(--pl-text-muted)]">
              {SAMPLE_API_KEYS.filter((k) => k.status === "active").length} active · {SAMPLE_API_KEYS.length} total
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="billing" className="mt-6" data-testid="apidash-billing">
          <Card title="Plan · Premier">
            <div className="text-sm text-[var(--pl-text)]">$129 / month · auto-renews on 2026-03-01</div>
            <div className="text-xs text-[var(--pl-text-muted)] mt-1.5">
              50,000 AI credits · unlimited projects · workspace seats: 5
            </div>
            <button
              data-testid="billing-upgrade-btn"
              className="mt-3 h-9 px-3 rounded-xl pl-btn-aqua text-xs"
            >
              Upgrade
            </button>
          </Card>
        </TabsContent>

        <TabsContent value="usage" className="mt-6" data-testid="apidash-usage">
          <Card title="Usage by provider (last 30 days)">
            <ul className="mt-2 space-y-2 text-sm">
              {[
                ["OpenAI · GPT-5.2", 18450, 60],
                ["Anthropic · Claude Sonnet", 12900, 42],
                ["Google · Gemini 2.5", 8200, 28],
                ["ElevenLabs · Voice", 320, 18],
                ["Nano Banana · Image", 540, 22],
              ].map(([label, used, pct]) => (
                <li key={label}>
                  <div className="flex items-center justify-between">
                    <span className="text-[var(--pl-text)]">{label}</span>
                    <span className="font-mono text-xs text-[var(--pl-text-muted)]">{used.toLocaleString()}</span>
                  </div>
                  <div className="mt-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full pl-signature-gradient" style={{ width: `${pct}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </TabsContent>

        <TabsContent value="keys" className="mt-6" data-testid="apidash-keys">
          <ApiKeysTable />
        </TabsContent>

        <TabsContent value="webhooks" className="mt-6" data-testid="apidash-webhooks">
          <WebhooksTable />
        </TabsContent>

        <TabsContent value="docs" className="mt-6 space-y-3" data-testid="apidash-docs">
          <Card title="Get started">
            <p className="text-sm text-[var(--pl-text-muted)] mt-1">
              Authenticate with a workspace API key. All endpoints are scoped to your workspace.
            </p>
            <pre className="mt-3 rounded-xl border border-[var(--pl-border)] bg-black/30 p-3 text-xs font-mono text-[var(--pl-text)] overflow-x-auto">
{`curl -X POST https://api.premierlab.ai/v1/generate \\
  -H "Authorization: Bearer pl_live_********" \\
  -H "Content-Type: application/json" \\
  -d '{"type":"book","prompt":"Operator playbook"}'`}
            </pre>
          </Card>
          <Card title="MCP endpoint">
            <p className="text-sm text-[var(--pl-text-muted)]">Use the MCP URL in your agent settings.</p>
            <pre className="mt-2 rounded-xl border border-[var(--pl-border)] bg-black/30 p-3 text-xs font-mono text-[var(--pl-text)] overflow-x-auto">
{`https://mcp.premierlab.ai/v1/workspace`}
            </pre>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Tab({ v, icon: I, children }) {
  return (
    <TabsTrigger
      value={v}
      data-testid={`api-tab-${v}`}
      className="data-[state=active]:bg-[var(--pl-aqua)]/15 data-[state=active]:text-[var(--pl-aqua)] data-[state=active]:shadow-none rounded-lg text-xs justify-start gap-1.5"
    >
      <I className="h-3.5 w-3.5" />
      <span className="truncate">{children}</span>
    </TabsTrigger>
  );
}

function Stat({ label, value, sub, tone }) {
  return (
    <div className="rounded-2xl p-4 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-text-muted)]">{label}</div>
      <div className={cn("font-display text-2xl font-semibold mt-1", tone === "bad" ? "text-rose-300" : "text-[var(--pl-text)]")}>
        {value}
      </div>
      {sub && <div className="text-[11px] text-[var(--pl-text-muted)] mt-0.5">{sub}</div>}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-2xl p-5 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">{title}</div>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function ApiKeysTable() {
  const [reveal, setReveal] = useState(null);
  return (
    <div className="rounded-2xl border border-[var(--pl-border)] bg-[var(--pl-surface)]/60 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--pl-border)]">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">API Keys</div>
        <button
          data-testid="apikey-create-btn"
          className="inline-flex items-center gap-2 h-9 px-3 rounded-xl pl-btn-aqua text-xs"
        >
          <Plus className="h-3.5 w-3.5" /> New Key
        </button>
      </div>
      <div className="px-5 py-2 text-[11px] text-[var(--pl-text-muted)]">
        Keys are shown masked. Full secret is displayed once at creation and never again.
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-[0.18em] text-[var(--pl-text-muted)]">
            <tr>
              {["Name", "Key (masked)", "Type", "Created", "Last used", "Status", "Actions"].map((h) => (
                <th key={h} className="text-left py-2 px-5 font-normal">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="text-[var(--pl-text)]">
            {SAMPLE_API_KEYS.map((k) => (
              <tr key={k.id} data-testid={`apikey-row-${k.id}`} className="border-t border-[var(--pl-border)]">
                <td className="py-3 px-5">{k.name}</td>
                <td className="px-5 font-mono text-xs text-[var(--pl-aqua)]">
                  <span className="inline-flex items-center gap-1.5">
                    {reveal === k.id ? "(unavailable — never stored)" : k.masked}
                    <button
                      data-testid={`apikey-reveal-${k.id}`}
                      onClick={() => setReveal((r) => (r === k.id ? null : k.id))}
                      className="text-[var(--pl-text-muted)] hover:text-[var(--pl-aqua)]"
                      aria-label="Reveal info"
                    >
                      {reveal === k.id ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    </button>
                  </span>
                </td>
                <td className="px-5">
                  <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-md border border-[var(--pl-border)] text-[var(--pl-text-muted)]">
                    {k.type}
                  </span>
                </td>
                <td className="px-5 text-xs text-[var(--pl-text-muted)]">{k.created_at}</td>
                <td className="px-5 text-xs text-[var(--pl-text-muted)]">{k.last_used}</td>
                <td className="px-5">
                  <span
                    className={cn(
                      "text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-md border",
                      k.status === "active"
                        ? "border-emerald-500/40 text-emerald-300"
                        : "border-rose-500/40 text-rose-300"
                    )}
                  >
                    {k.status}
                  </span>
                </td>
                <td className="px-5">
                  <div className="flex items-center gap-1">
                    <button
                      data-testid={`apikey-rotate-${k.id}`}
                      className="h-8 w-8 grid place-items-center rounded-lg border border-[var(--pl-border)] text-[var(--pl-text-muted)] hover:text-[var(--pl-aqua)]"
                      aria-label="Rotate"
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                    </button>
                    <button
                      data-testid={`apikey-delete-${k.id}`}
                      className="h-8 w-8 grid place-items-center rounded-lg border border-[var(--pl-border)] text-[var(--pl-text-muted)] hover:text-rose-400"
                      aria-label="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function WebhooksTable() {
  const [url, setUrl] = useState("");
  return (
    <div className="space-y-4">
      <div className="rounded-2xl p-5 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">Add webhook endpoint</div>
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <input
            data-testid="webhook-url-input"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com/hooks/premierlab"
            style={{ background: "color-mix(in srgb, var(--pl-surface) 70%, transparent)", color: "var(--pl-text)" }}
            className="flex-1 rounded-xl border border-[var(--pl-border)] px-3 h-11 text-sm focus:outline-none focus:border-[var(--pl-aqua)]/50"
          />
          <button
            data-testid="webhook-verify-btn"
            className="h-11 px-4 rounded-xl pl-btn-aqua text-sm inline-flex items-center gap-2"
          >
            <Check className="h-4 w-4" /> Verify
          </button>
        </div>
        <div className="text-[11px] text-[var(--pl-text-muted)] mt-2">
          We will send a signed POST. Your endpoint must return 200 within 5s.
        </div>
      </div>

      <div className="rounded-2xl border border-[var(--pl-border)] bg-[var(--pl-surface)]/60 overflow-hidden">
        <div className="px-5 py-3 border-b border-[var(--pl-border)] text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">
          Registered Webhooks
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.18em] text-[var(--pl-text-muted)]">
              <tr>
                {["Name", "URL", "Status", "Last delivery", ""].map((h) => (
                  <th key={h} className="text-left py-2 px-5 font-normal">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="text-[var(--pl-text)]">
              {SAMPLE_WEBHOOKS.map((w) => (
                <tr key={w.id} data-testid={`webhook-row-${w.id}`} className="border-t border-[var(--pl-border)]">
                  <td className="py-3 px-5">{w.name}</td>
                  <td className="px-5 font-mono text-xs text-[var(--pl-text-muted)] max-w-[280px] truncate">{w.url}</td>
                  <td className="px-5">
                    <span
                      className={cn(
                        "text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-md border",
                        w.status === "verified"
                          ? "border-emerald-500/40 text-emerald-300"
                          : "border-[var(--pl-aqua)]/40 text-[var(--pl-aqua)]"
                      )}
                    >
                      {w.status}
                    </span>
                  </td>
                  <td className="px-5 text-xs text-[var(--pl-text-muted)]">{w.last_delivery}</td>
                  <td className="px-5">
                    <button
                      data-testid={`webhook-copy-${w.id}`}
                      onClick={() => navigator.clipboard?.writeText(w.url)}
                      className="h-8 w-8 grid place-items-center rounded-lg border border-[var(--pl-border)] text-[var(--pl-text-muted)] hover:text-[var(--pl-aqua)]"
                      aria-label="Copy URL"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
